import './formats.js';
import { Type } from '@sinclair/typebox';
const Cell = Type.Object({ cellX: Type.Integer({ minimum: 0 }), cellY: Type.Integer({ minimum: 0 }) });
const Id = Type.String({ format: 'uuid' });
const Duration = Type.Union([Type.Integer({ minimum: 1 }), Type.Null()]);
/** Trees designated in wood cases, in the player's order
 * (docs/SPEC-BOIS-ARBRE-UNITE.md): each case must also be listed in `wood`.
 * A case listed without designation means all of its trees. The tree index is
 * the case's own (RC1 `tree_indices`, or 0 to N−1 without RC1 forest). */
export const WoodTreesSchema = Type.Array(Type.Object({
    featureId: Id,
    treeIndices: Type.Array(Type.Integer({ minimum: 0 }), { minItems: 1, maxItems: 256 }),
}, { additionalProperties: false }), { maxItems: 64 });
/** Refusal of a selected element; `treeIndex` names a refused tree of the case `key`. */
const RefusalSchema = Type.Object({
    key: Type.String(),
    reason: Type.String(),
    treeIndex: Type.Optional(Type.Integer({ minimum: 0 })),
});
export const ExploitationRequestSchema = Type.Object({
    commandId: Id,
    workerCap: Type.Integer({ minimum: 1 }),
    durationMs: Duration,
    cohortId: Type.Union([Id, Type.Null()]),
    initialAdmission: Type.Optional(Type.Union([Type.Literal('required'), Type.Literal('allow-wait')])),
    woodMode: Type.Union([Type.Literal('cut'), Type.Literal('clear')]),
    gardens: Type.Array(Cell, { maxItems: 100 }),
    wood: Type.Array(Id, { maxItems: 64 }),
    woodTrees: Type.Optional(WoodTreesSchema),
    stone: Type.Array(Id, { maxItems: 64 }),
    activityCaps: Type.Optional(Type.Object({
        wood: Type.Integer({ minimum: 0, maximum: 10 }),
        stone: Type.Integer({ minimum: 0, maximum: 10 }),
        gardens: Type.Integer({ minimum: 0, maximum: 1 }),
    })),
    activityDurations: Type.Optional(Type.Object({ gardens: Duration, wood: Duration, stone: Duration })),
}, { additionalProperties: false });
export const ExploitationPreviewSchema = Type.Object({
    gardens: Type.Array(Cell),
    wood: Type.Array(Id),
    /** Trees kept for each designated case, in their order (stumps left out when cutting). */
    woodTrees: Type.Optional(WoodTreesSchema),
    stone: Type.Array(Id),
    excluded: Type.Array(RefusalSchema),
    gardenReturnMs: Type.Union([Type.Integer({ minimum: 0 }), Type.Null()]),
    estimates: Type.Object({
        carrot: Type.Integer({ minimum: 0 }),
        wood: Type.Integer({ minimum: 0 }),
        stone: Type.Integer({ minimum: 0 }),
    }),
    workforce: Type.Object({
        structuralMax: Type.Integer({ minimum: 0 }),
        autoWorkerCap: Type.Integer({ minimum: 0 }),
        availableNow: Type.Integer({ minimum: 0 }),
        allocation: Type.Object({
            gardens: Type.Integer({ minimum: 0, maximum: 1 }),
            wood: Type.Integer({ minimum: 0, maximum: 10 }),
            stone: Type.Integer({ minimum: 0, maximum: 10 }),
        }),
        reason: Type.Union([Type.String(), Type.Null()]),
    }),
});
export const ExploitationOrderSchema = Type.Object({
    id: Id,
    workerCap: Type.Integer({ minimum: 1 }),
    mobilized: Type.Integer({ minimum: 0 }),
    confirmedAt: Type.String({ format: 'date-time' }),
    deadline: Type.Union([Type.String({ format: 'date-time' }), Type.Null()]),
    status: Type.Union([
        Type.Literal('waiting'),
        Type.Literal('active'),
        Type.Literal('returning'),
        Type.Literal('completed'),
    ]),
    gardenStatus: Type.Union([
        Type.Literal('pending'),
        Type.Literal('active'),
        Type.Literal('completed'),
        Type.Literal('closed'),
    ]),
    worksiteIds: Type.Array(Id),
});
/** Budget per pending admission, including people already in transport. */
export function exploitationAdmissionCap(total, used, openSites, gardenReserved, childCap = 10, gardenActive = false) {
    const available = Math.max(0, total - used - (gardenReserved ? 1 : 0));
    const share = Math.max(1, Math.ceil((total - (gardenReserved || gardenActive ? 1 : 0)) / Math.max(1, openSites)));
    return Math.min(childCap, available, share);
}
const WoodModeSchema = Type.Union([Type.Literal('cut'), Type.Literal('clear')]);
// The player supplies targets only. Workforce and aggregation belong to the server.
export const HarvestIntentSchema = Type.Object({
    commandId: Id,
    gardens: Type.Array(Cell, { maxItems: 100 }),
    wood: Type.Array(Id, { maxItems: 64 }),
    woodTrees: Type.Optional(WoodTreesSchema),
    /** What the wood queue does with the designated trees and stumps (slice 2e):
     * `cut` (default) takes their wood; `clear` takes their wood, then removes
     * their stumps. Clearing includes cutting: a tree queued for its wood and
     * designated again for clearing is cleared; the reverse adds nothing. */
    woodMode: Type.Optional(WoodModeSchema),
    stone: Type.Array(Id, { maxItems: 64 }),
}, { additionalProperties: false });
export const HarvestReceiptSchema = Type.Object({
    pending: Type.Optional(Type.Boolean()),
    commandId: Id,
    accepted: Type.Array(Type.Object({
        key: Type.String(),
        cellX: Type.Integer(),
        cellY: Type.Integer(),
        resource: Type.Union([Type.Literal('carrot'), Type.Literal('wood'), Type.Literal('stone')]),
        amount: Type.Integer({ minimum: 0 }),
        /** Trees of the case this intent added to the queue (clearing: the trees it
         * queued for clearing), in their order; absent for every tree of the case. */
        treeIndices: Type.Optional(Type.Array(Type.Integer({ minimum: 0 }))),
        /** `clear` when the wood was queued for clearing; absent: cutting. */
        woodMode: Type.Optional(Type.Literal('clear')),
    })),
    refused: Type.Array(RefusalSchema),
});
/** What a cancellation did to one tree (or stump) of the gesture:
 * - `removed`: this cancellation took it out of the queue;
 * - `in-progress`: a lot of the village works on it: only that lot is
 *   delivered, then the tree leaves the queue (its trunk keeps the wood left,
 *   its stump stays), like stopping a clearing;
 * - `queued`: it stays queued for another gesture or an earlier queue (`mode`);
 * - `not-queued`: nothing was left to withdraw (its work is done, or its case
 *   left the queue before). */
export const HarvestCancelOutcomeSchema = Type.Union([
    Type.Literal('removed'),
    Type.Literal('in-progress'),
    Type.Literal('queued'),
    Type.Literal('not-queued'),
]);
/** Receipt of `POST …/harvest-intents/:commandId/cancel` (slice 2e), stored on
 * the first call and answered unchanged on every replay. Only the wood of the
 * gesture is withdrawn; its gardens and stone are left as they are. */
export const HarvestCancelReceiptSchema = Type.Object({
    /** The cancelled gesture: the `commandId` of its harvest intent. */
    commandId: Id,
    /** The gesture had not been processed yet: its wood will never be queued
     * (its intent receipt refuses each case with `cancelled`). */
    beforeQueue: Type.Optional(Type.Literal(true)),
    trees: Type.Array(Type.Object({
        featureId: Id,
        treeIndex: Type.Integer({ minimum: 0 }),
        outcome: HarvestCancelOutcomeSchema,
        /** Its mode in the queue after the cancellation, when it stays queued (`queued`). */
        mode: Type.Optional(WoodModeSchema),
    }), { maxItems: 16384 }),
});
//# sourceMappingURL=exploitation.js.map